import type { HybridReportLibraryBlock } from '../../../data/ServerApiClient'

type ApplyHybridReportAiOperationsOptions = {
  spec: any
  operations: any[]
  library: HybridReportLibraryBlock[]
  normalize: (spec: any) => any
  patch: (spec: any, operation: any) => any
  addCatalogBlock: (
    spec: any,
    catalog: HybridReportLibraryBlock,
    requested: Record<string, any>,
  ) => Promise<any>
}

const sameSpec = (left: any, right: any): boolean => JSON.stringify(left) === JSON.stringify(right)

/**
 * Apply a server-approved AI operation envelope without silently discarding
 * stale or inapplicable instructions. A partial/no-op edit must not be saved as
 * a successful, undoable report revision.
 */
export async function applyHybridReportAiOperations({
  spec,
  operations,
  library,
  normalize,
  patch,
  addCatalogBlock,
}: ApplyHybridReportAiOperationsOptions): Promise<any> {
  const original = normalize(spec)
  let next = original
  let appliedOperations = 0
  const libraryById = new Map(library.map((item) => [item.library_id, item]))

  for (const operation of operations || []) {
    if (operation?.op === 'add') {
      const requested = operation.block && typeof operation.block === 'object' ? operation.block : {}
      const libraryId = String(requested.library_id || '').trim()
      if (!libraryId) {
        throw new Error('AI add operation is missing its governed library identity. No report changes were saved.')
      }
      const catalog = libraryById.get(libraryId)
      if (!catalog) {
        throw new Error('AI requested a governed block that is not available for this workbook. No report changes were saved.')
      }
      next = await addCatalogBlock(next, catalog, requested)
      appliedOperations += 1
      continue
    }

    const blockId = String(operation?.block_id || '').trim()
    const current = next.blocks?.find((block: any) => block.id === blockId)
    if (!current) {
      throw new Error('AI targeted a report block that no longer exists. Refresh the draft and try again; no report changes were saved.')
    }

    if (operation.op === 'update') {
      const changes = { ...(operation.changes || {}) }
      if (current.origin !== 'web_library' || current.kind !== 'text') delete changes.content
      next = patch(next, { ...operation, block_id: blockId, changes })
    } else if (operation.op === 'move' || operation.op === 'remove') {
      next = patch(next, { ...operation, block_id: blockId })
    } else {
      throw new Error('AI returned an unsupported report edit. No report changes were saved.')
    }
    appliedOperations += 1
  }

  if (!appliedOperations) {
    throw new Error('AI did not return any report edits. No report changes were saved.')
  }

  const normalized = normalize(next)
  if (sameSpec(original, normalized)) {
    throw new Error('AI did not produce an applicable report change. No report changes were saved.')
  }
  return normalized
}
